/**
 * Per-contract-type metadata schema.
 *
 * Drives which metadata fields are shown and how the two party fields
 * (vendor_name / customer_name) and the value field are labelled, so the
 * Contract Detail view adapts to the detected contract type — e.g. an NDA
 * hides Value and labels the parties Discloser / Recipient, employment shows
 * Employer / Employee + Compensation, a lease shows Lessor / Lessee + Rent.
 *
 * The underlying columns never change (vendor_name = the "provider" side,
 * customer_name = the "receiver" side); only their presentation does.
 */

export interface ContractTypeSchema {
  /** Human label for the type itself. */
  label: string;
  /** Label for the vendor_name column. */
  vendorLabel: string;
  /** Label for the customer_name column. */
  customerLabel: string;
  /** Label for the contract_value column. */
  valueLabel: string;
  /** Whether the monetary value + currency are relevant for this type. */
  showValue: boolean;
  /** Whether the term (start/end date) is relevant. */
  showTerm: boolean;
  /** Whether auto-renewal + notice period are relevant. */
  showRenewal: boolean;
}

const DEFAULT_SCHEMA: ContractTypeSchema = {
  label: 'Other',
  vendorLabel: 'Party A', customerLabel: 'Party B', valueLabel: 'Value',
  showValue: true, showTerm: true, showRenewal: true,
};

const SCHEMAS: Record<string, ContractTypeSchema> = {
  commercial: {
    label: 'Commercial',
    vendorLabel: 'Vendor', customerLabel: 'Customer', valueLabel: 'Value',
    showValue: true, showTerm: true, showRenewal: true,
  },
  service_agreement: {
    label: 'Service Agreement',
    vendorLabel: 'Provider', customerLabel: 'Client', valueLabel: 'Value',
    showValue: true, showTerm: true, showRenewal: true,
  },
  nda: {
    label: 'NDA',
    vendorLabel: 'Discloser', customerLabel: 'Recipient', valueLabel: 'Value',
    showValue: false, showTerm: true, showRenewal: true,
  },
  employment: {
    label: 'Employment',
    vendorLabel: 'Employer', customerLabel: 'Employee', valueLabel: 'Compensation',
    showValue: true, showTerm: true, showRenewal: false,
  },
  lease: {
    label: 'Lease',
    vendorLabel: 'Lessor', customerLabel: 'Lessee', valueLabel: 'Rent',
    showValue: true, showTerm: true, showRenewal: true,
  },
  dpa: {
    label: 'Data Processing (DPA)',
    vendorLabel: 'Processor', customerLabel: 'Controller', valueLabel: 'Value',
    showValue: false, showTerm: true, showRenewal: true,
  },
  consumer: {
    label: 'Consumer (B2C)',
    vendorLabel: 'Trader', customerLabel: 'Consumer', valueLabel: 'Price',
    showValue: true, showTerm: true, showRenewal: true,
  },
  partnership: {
    label: 'Corporate / Partnership',
    vendorLabel: 'Partner A', customerLabel: 'Partner B', valueLabel: 'Capital contribution',
    showValue: true, showTerm: true, showRenewal: false,
  },
  other: DEFAULT_SCHEMA,
};

/** Resolve the schema for a contract type, falling back to a generic layout. */
export function getContractTypeSchema(type: string | null | undefined): ContractTypeSchema {
  return type != null && type in SCHEMAS ? SCHEMAS[type] : DEFAULT_SCHEMA;
}

/**
 * Options for the contract-type selector, in display order.
 *
 * Mirrors CANONICAL_CONTRACT_TYPES in the backend's contract_type_map.py and
 * the DB CHECK constraints on contracts.contract_type and
 * jobs.declared_contract_type. Every value except 'other' has a row per
 * jurisdiction in the legal_basis table.
 */
export const CONTRACT_TYPE_OPTIONS: { value: string; label: string }[] = [
  'commercial', 'service_agreement', 'nda', 'employment', 'lease',
  'dpa', 'consumer', 'partnership', 'other',
].map((value) => ({ value, label: SCHEMAS[value].label }));

/** One-line hint per type, shown under the label in the upload selector. */
export const CONTRACT_TYPE_HINTS: Record<string, string> = {
  commercial: 'Purchase, supply, distribution, reseller, or agency agreements',
  service_agreement: 'MSAs, statements of work, consulting, SaaS, maintenance',
  nda: 'Confidentiality and non-disclosure agreements',
  employment: 'Employment contracts, offer letters, contracts of service',
  lease: 'Property leases, rental and tenancy agreements',
  dpa: 'GDPR Article 28 data processing and data transfer agreements',
  consumer: 'B2C terms, end-user licences, consumer sales',
  partnership: 'Shareholder, partnership, and joint venture agreements',
  other: 'Anything that does not fit the categories above',
};
